<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import logo from '../../assets/home/logo.webp';
import { useLocale } from '../../i18n/use-locale';

const { t } = useI18n();
const { current, set } = useLocale();

const year = new Date().getFullYear();

// Display label per locale (Czech shows the country-style "CZ", not ISO "CS").
const localeLabel: Record<string, string> = { en: 'EN', cs: 'CZ' };

// Footer navigation columns (Figma "BAR / FOOTER"). `to` points at the
// intended routes; some destinations (legal pages, support) aren't built yet.
const columns = [
  {
    heading: 'shop',
    links: [
      { key: 'marketplace', to: '/' },
      { key: 'catalog', to: '/catalog' },
      { key: 'articles', to: '/articles' },
    ],
  },
  {
    heading: 'legal',
    links: [
      { key: 'terms', to: '/terms' },
      { key: 'privacy', to: '/privacy' },
      { key: 'cookies', to: '/cookies' },
    ],
  },
  {
    heading: 'help',
    links: [
      { key: 'support', to: '/support' },
      { key: 'faq', to: '/faq' },
    ],
  },
];
</script>

<template>
  <footer class="relative z-10 border-t border-term-line bg-term-bg font-mono text-term-ink">
    <div class="mx-auto flex max-w-[1280px] flex-col gap-12 px-8 py-12">
      <!-- Top: brand/legal + columns -->
      <div class="flex flex-col gap-10 lg:flex-row lg:items-start lg:justify-between">
        <!-- Left column (~40%) -->
        <div class="lg:w-[40%] lg:max-w-[486px]">
          <router-link to="/" class="inline-flex" aria-label="PixelKey">
            <img :src="logo" alt="PixelKey" class="h-[29px] w-auto" />
          </router-link>

          <p class="mt-6 max-w-[384px] font-['Inter',sans-serif] text-[14px] leading-[21px] text-term-muted">
            {{ t('footer.tagline') }}
          </p>

          <div class="mt-8 flex flex-col gap-1 text-[12px] leading-[12px] text-term-dim">
            <p>PixelKey Placeholder Ltd. // Placeholder address, Riga, Latvia</p>
            <p>VAT: LV40012345678 // Reg. No.: 40012345678</p>
            <p class="mt-2 text-term-green/80">SYSTEM STATUS: ALL SYSTEMS OPERATIONAL</p>
          </div>
        </div>

        <!-- Right side: language switcher + nav columns -->
        <div class="flex flex-col gap-8 lg:w-[665px]">
          <!-- Language switcher -->
          <div class="flex justify-start lg:justify-end">
            <div class="inline-flex border border-term-green p-px text-[12px]">
              <button
                v-for="loc in (['en', 'cs'] as const)"
                :key="loc"
                type="button"
                class="px-3 py-1 transition-colors"
                :class="
                  current === loc
                    ? 'bg-term-green font-bold text-term-green-on'
                    : 'font-medium text-term-ink hover:text-term-green'
                "
                @click="set(loc)"
              >
                {{ current === loc ? `[ ${localeLabel[loc]} ]` : localeLabel[loc] }}
              </button>
            </div>
          </div>

          <!-- Navigation columns -->
          <div class="grid grid-cols-2 gap-x-12 gap-y-10 sm:grid-cols-3">
            <div v-for="col in columns" :key="col.heading" class="flex flex-col gap-4">
              <h4 class="text-[12px] font-bold uppercase leading-[12px] tracking-[0.1em] text-term-green">
                // {{ t(`footer.${col.heading}`) }}
              </h4>
              <ul class="flex flex-col gap-3">
                <li v-for="link in col.links" :key="link.key">
                  <router-link
                    :to="link.to"
                    class="block py-0.5 text-[14px] font-medium leading-[14px] text-term-ink transition-colors hover:text-term-green"
                  >
                    {{ t(`footer.${link.key}`) }}
                  </router-link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom strip -->
      <div
        class="flex flex-col gap-2 border-t border-term-line-2 pt-6 text-[12px] leading-[12px] text-term-dim sm:flex-row sm:items-center sm:justify-between"
      >
        <p>pixelkey@web:~$ uptime <span class="text-term-green">// 99.99%</span></p>
        <p class="sm:text-right">© {{ year }} PIXELKEY // SYSTEM STATUS: NOMINAL // BUILD: v2.4.0-STABLE</p>
      </div>
    </div>
  </footer>
</template>
